import { useEffect, useMemo, useState } from "react";
import { Factory, TrendingUp, Table } from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";

const PLANT_COLOURS = [
  "#34D399", "#60A5FA", "#94A3B8", "#EC4899",
  "#FBBF24", "#A78BFA", "#F87171", "#00D4FF",
];

const fmtInt = (n) => (n === null || n === undefined || isNaN(n) ? "—" : Number(n).toLocaleString());
// Whole-number SF (per user request — spell out, no "K" abbreviation)
const fmtSF = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

/**
 * Sub-analytics for the Final QC Pipeline card.
 * Three panels:
 *   1) Piece Distribution by Plant (donut + legend)
 *   2) Detection Gap Days by Plant (grouped horizontal bar: Avg + Max)
 *   3) Detailed Status by Plant (table with FULL SF, no K abbreviation)
 * Each panel owns its own DateFilter and only fetches after the user
 * interacts with it (Quick Load or a filter change) — mirrors the parent
 * QC-pipeline card's behaviour.
 */
export default function FinalQcAnalytics({ showMexico = false }) {
  return (
    <div className="space-y-6" data-testid="final-qc-analytics">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PieceDistribution showMexico={showMexico} />
        <GapDaysByPlant  showMexico={showMexico} />
      </div>
      <DetailedStatusTable showMexico={showMexico} />
    </div>
  );
}

/* Shared hook — interacted-gated fetch of /final-qc/by-plant. */
function useByPlant(showMexico) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [interacted, setInteracted] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    if (!interacted) return;
    let cancelled = false;
    setLoading(true);
    api.get("/final-qc/by-plant", { params })
      .then((r) => !cancelled && setRows(r.data.plants || []))
      .catch((e) => !cancelled && toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, interacted]);

  const onFilterChange = (v) => { setInteracted(true); setFilter(v); };
  const quickLoad = () => { setInteracted(true); setFilter({ preset: "last30" }); };
  return { filter, onFilterChange, quickLoad, rows, loading, interacted };
}

function CardShell({ title, icon, accent, filter, onFilterChange, quickLoad, interacted, children, testId }) {
  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden" data-testid={testId}>
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)` }} />
      <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-heading font-bold text-lg">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <DateFilter value={filter} onChange={onFilterChange} />
          {!interacted && (
            <button data-testid={`${testId}-quick-load`} onClick={quickLoad}
                    className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-mono bg-[#A78BFA] text-[#0A0B10] hover:bg-[#A78BFA]/80 transition-colors">
              Quick Load · Last 30 Days
            </button>
          )}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function EmptyPrompt({ label = "Pick a date range or click Quick Load to fetch data." }) {
  return (
    <div className="py-10 text-center text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
  );
}

/* ---------------------------------------------------------------- */
/* 1) Piece Distribution by Plant (donut + legend)                  */
/* ---------------------------------------------------------------- */
function PieceDistribution({ showMexico }) {
  const { filter, onFilterChange, quickLoad, rows, interacted } = useByPlant(showMexico);
  const data = rows.map((r, i) => ({
    name: r.plant, value: r.pieces, colour: PLANT_COLOURS[i % PLANT_COLOURS.length],
  }));
  return (
    <CardShell testId="final-qc-piece-distribution"
               title="Piece Distribution by Plant"
               icon={<Factory size={16} className="text-[#34D399]" />}
               accent="#34D399"
               filter={filter} onFilterChange={onFilterChange}
               quickLoad={quickLoad} interacted={interacted}>
      {!interacted ? <EmptyPrompt /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="h-[240px]">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {data.map((d, i) => <Cell key={i} fill={d.colour} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }}
                         formatter={(v) => fmtInt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1">
            {data.map((d) => (
              <div key={d.name} className="flex items-center justify-between border border-border/40 bg-[#0A0B10] px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.colour }} />
                  <span>{d.name}</span>
                </div>
                <span className="font-mono tabular-nums text-white">{fmtInt(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardShell>
  );
}

/* ---------------------------------------------------------------- */
/* 2) Detection Gap Days by Plant (blue avg + red max horizontal)   */
/* ---------------------------------------------------------------- */
function GapDaysByPlant({ showMexico }) {
  const { filter, onFilterChange, quickLoad, rows, interacted } = useByPlant(showMexico);
  const data = rows.map((r) => ({ plant: r.plant, avg: r.avg_gap, max: r.max_gap }));
  return (
    <CardShell testId="final-qc-gap-days"
               title="Detection Gap Days by Plant"
               icon={<TrendingUp size={16} className="text-[#F87171]" />}
               accent="#F87171"
               filter={filter} onFilterChange={onFilterChange}
               quickLoad={quickLoad} interacted={interacted}>
      {!interacted ? <EmptyPrompt /> : (
        <div className="h-[240px]">
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis type="number" stroke="#94A3B8" fontSize={11} />
              <YAxis type="category" dataKey="plant" stroke="#94A3B8" fontSize={11} width={110}
                     tickFormatter={(v) => (v && v.length > 14 ? v.slice(0, 13) + "…" : v)} />
              <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="avg" fill="#60A5FA" name="Avg Gap" radius={[0, 4, 4, 0]} />
              <Bar dataKey="max" fill="#F87171" name="Max Gap" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </CardShell>
  );
}

/* ---------------------------------------------------------------- */
/* 3) Detailed Status by Plant (table, full SF spelled out)         */
/* ---------------------------------------------------------------- */
function DetailedStatusTable({ showMexico }) {
  const { filter, onFilterChange, quickLoad, rows, interacted } = useByPlant(showMexico);
  return (
    <CardShell testId="final-qc-detailed-status"
               title="Detailed Status by Plant"
               icon={<Table size={16} className="text-[#00D4FF]" />}
               accent="#00D4FF"
               filter={filter} onFilterChange={onFilterChange}
               quickLoad={quickLoad} interacted={interacted}>
      {!interacted ? <EmptyPrompt /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="py-2 px-3">Plant</th>
                <th className="py-2 px-3 text-right">Pieces</th>
                <th className="py-2 px-3 text-right">Total SF</th>
                <th className="py-2 px-3 text-right">Passed</th>
                <th className="py-2 px-3 text-right">Unchecked</th>
                <th className="py-2 px-3 text-right">Pass Rate</th>
                <th className="py-2 px-3 text-right">Avg Gap</th>
                <th className="py-2 px-3 text-right">Max Gap</th>
              </tr>
            </thead>
            <tbody data-testid="final-qc-detailed-status-rows">
              {rows.map((r) => (
                <tr key={r.plant} className="border-b border-border/40 hover:bg-[#0A0B10]/40">
                  <td className="py-2 px-3">
                    <span className="inline-block px-2 py-0.5 text-[11px] font-mono border border-border/60">
                      {r.plant}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums">{fmtInt(r.pieces)}</td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums" data-testid={`final-qc-total-sf-${r.plant}`}>
                    {fmtSF(r.total_sf)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums">{fmtInt(r.passed)}</td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums">{fmtInt(r.unchecked)}</td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums"
                      style={{ color: r.pass_rate >= 95 ? "#34D399" : r.pass_rate >= 80 ? "#FBBF24" : "#F87171" }}>
                    {r.pass_rate}%
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums">{r.avg_gap}d</td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums">{r.max_gap}d</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">No plants match filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </CardShell>
  );
}
